import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { publicApi, formatPublicError, isValidMobile, honeypotOf } from "../../lib/publicApi";
import usePublicSettings, { fmtMoney, pickPhoto, photoSrcSet } from "../../lib/usePublicSettings";
import VehicleCard from "../../components/VehicleCard";
import { ReserveOnline } from "../../components/ReserveOnline";
import { useT, lbl } from "../../lib/i18n";
import { Phone, MessageCircle, Share2, ChevronLeft } from "lucide-react";

import { tr } from "../../lib/i18n";
export default function VehicleDetailPage() {
  const { ident } = useParams();
  const { t } = useT();
  const { settings } = usePublicSettings();
  const [v, setV] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [kind, setKind] = useState("vehicle");

  useEffect(() => {
    setNotFound(false);
    publicApi
      .get(`/vehicles/${ident}`)
      .then((r) => {
        setV(r.data);
        // OG meta
        document.title = `${r.data.make} ${r.data.model} ${r.data.model_year} — ${settings?.showroom_name || ""}`;
        const desc = r.data.description || `${r.data.make} ${r.data.model} ${r.data.model_year} for sale`;
        let m = document.querySelector('meta[name="description"]');
        if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
        m.content = desc;
      })
      .catch((e) => setNotFound(e?.response?.status === 404));
    publicApi.get(`/vehicles/${ident}/similar`).then((r) => setSimilar(r.data.items || [])).catch(() => {});
  }, [ident, settings?.showroom_name]);

  if (notFound) {
    return (
      <div className="public-container" style={{ padding: "80px 20px", textAlign: "center" }}>
        <h2 style={{ fontFamily: "Outfit" }}>{t("px.not_available")}</h2>
        <Link to="/cars" className="pub-cta" style={{ display: "inline-block", marginTop: 20 }}>{t("px.browse_other")}</Link>
      </div>
    );
  }
  if (!v) return <div className="public-container" style={{ padding: "80px 20px" }}>{tr("x.loading")}</div>;

  const photos = v.photos || [];
  const activePhoto = photos[activeIdx];
  const mainImg = activePhoto?.variants?.full || activePhoto?.url || pickPhoto(v, "full");
  const mainSrcSet = photoSrcSet(activePhoto, "(max-width: 900px) 100vw, 60vw");
  const price = v.public_price != null ? fmtMoney(v.public_price, settings) : t("vehicle.price_on_request");
  const wa = settings?.whatsapp ? settings.whatsapp.replace(/[^\d]/g, "") : "";
  const waLink = wa ? `https://wa.me/${wa}?text=${encodeURIComponent(`Interested in ${v.stock_number} · ${v.make} ${v.model} ${v.model_year}`)}` : "#";

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!isValidMobile(form.mobile)) { toast.error(t("enquiry.mobile_invalid")); return; }
    setSending(true);
    try {
      await publicApi.post("/enquiries", {
        kind, name: form.name, mobile: form.mobile, email: form.email,
        message: form.message, vehicle_id: v.stock_number, honeypot: honeypotOf(e),
      });
      toast.success(t("enquiry.success"));
      setForm({ name: "", mobile: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatPublicError(err));
    } finally {
      setSending(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: `${v.make} ${v.model}`, url }); } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success(t("px.link_copied"));
      } catch {}
    }
  };

  const specs = [
    ["stock", v.stock_number], ["make", v.make], ["model", v.model], ["year", v.model_year],
    ["mileage", `${(v.mileage || 0).toLocaleString()} ${tr("x.km_unit")}`],
    ["body_type", lbl(v.body_type)], ["transmission", lbl(v.transmission)], ["fuel_type", lbl(v.fuel_type)],
    ["engine", v.engine], ["cylinders", v.cylinders], ["exterior_color", v.exterior_color],
    ["interior_color", v.interior_color], ["specification", lbl(v.specification)],
  ].filter(([, val]) => val != null && val !== "");

  return (
    <div data-testid="vehicle-detail-page">
      {/* Dark title band */}
      <div className="vd-band on-dark">
        <div className="public-container" style={{ padding: "140px 20px 32px" }}>
          <Link to="/cars" className="back-link" data-testid="vd-back">
            <ChevronLeft size={16} className="mirror-icon" /> {t("vehicle.back_to_listing")}
          </Link>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap", marginTop: 24 }}>
            <div>
              <span className={`pill-status st-${(v.status || "available").toLowerCase()}`}>
                <span className="dot" /> {lbl(v.status) || tr("x.available")}
              </span>
              <h1 style={{ fontSize: "clamp(44px, 5.4vw, 80px)", fontWeight: 200, letterSpacing: "-0.03em", lineHeight: 0.98, margin: "16px 0 0", color: "#fff" }}>
                <span style={{ fontWeight: 600 }}>{v.make} {v.model}</span> <span style={{ opacity: 0.7 }}>{lbl(v.specification || v.body_type) || ""}</span>
              </h1>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginTop: 10 }}>
                {t("px.stock_line", { s: `\u2068${v.stock_number}\u2069`, y: `\u2068${v.model_year}\u2069`, km: `\u2068${(v.mileage || 0).toLocaleString("en-US")}\u2069` })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="public-container" style={{ padding: "48px 20px 100px" }}>
        <div className="vd-grid">
          <div>
            <div className="gallery-main" data-testid="gallery-main">
              {mainImg ? <img src={activePhoto?.variants?.card || mainImg} {...mainSrcSet} width={1200} height={900} alt={v.make} decoding="async" /> : <div className="vcard-placeholder">{v.make}</div>}
            </div>
            {photos.length > 1 ? (
              <div className="gallery-thumbs" data-testid="gallery-thumbs">
                {photos.map((p, i) => (
                  <button key={i} onClick={() => setActiveIdx(i)} className={i === activeIdx ? "active" : ""} data-testid={`thumb-${i}`}>
                    <img src={p.variants?.thumbnail || p.thumbnail_url || p.url} width={120} height={90} alt="" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            ) : null}

            {v.description ? (
              <div style={{ marginTop: 48 }}>
                <div className="kicker" style={{ color: "var(--grey-600)" }}>{t("px.about")}</div>
                <p dir="auto" style={{ color: "var(--ink)", lineHeight: 1.65, whiteSpace: "pre-wrap", fontSize: 16, marginTop: 10 }}>{v.description}</p>
              </div>
            ) : null}

            <div style={{ marginTop: 48 }}>
              <div className="kicker" style={{ color: "var(--grey-600)" }}>{t("px.specs")}</div>
              <div className="spec-grid-ruled" data-testid="spec-table">
                {specs.map(([k, val]) => (
                  <div key={k} className="spec-cell">
                    <div className="spec-k">{t(`vehicle.${k /* i18n-keys: stock,make,model,year,mileage,body_type,transmission,fuel_type,engine,cylinders,exterior_color,interior_color,specification */}`)}</div>
                    <div className="spec-v">{val}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="vd-sidebar">
            <div className="vd-priceblock sticky-price" data-testid="price-block">
              <div className="kicker" style={{ color: "var(--grey-600)" }}>{t("px.price")}</div>
              <div className="vd-price" data-testid="vd-price" style={{ marginTop: 10, fontSize: 48, fontWeight: 200 }}>{price}</div>
              <div style={{ color: "var(--grey-600)", fontSize: 13, marginTop: 6 }}>
                {t("px.per_month", { n: Math.round(((v.public_price || 0) * 0.8 * (1 + 0.045 * 4)) / 48).toLocaleString("en-US") })}
              </div>

              <div style={{ display: "grid", gap: 8, marginTop: 24 }}>
                <button type="button" className="pub-cta primary big full" onClick={() => { setKind("test_drive"); document.querySelector('[data-testid="enquiry-form"]')?.scrollIntoView({ behavior: "smooth" }); }} data-testid="btn-book-test-drive">
                  {t("vehicle.book_test_drive")}
                </button>
                {wa ? (
                  <a href={waLink} target="_blank" rel="noopener noreferrer" className="pub-cta wa big full" data-testid="detail-wa">
                    <MessageCircle size={16} /> {t("vehicle.whatsapp")}
                  </a>
                ) : null}
                {settings?.phone ? (
                  <a href={`tel:${settings.phone}`} className="pub-cta dark big full" data-testid="detail-call">
                    <Phone size={16} /> <span dir="ltr" className="ltr">{settings.phone}</span>
                  </a>
                ) : null}
              </div>

              <ul style={{ listStyle: "none", padding: 0, margin: "28px 0 0", display: "grid", gap: 10, fontSize: 13, color: "var(--grey-600)" }}>
                <li>✓ {t("px.p1_t")}</li>
                <li>✓ {t("px.perk_2")}</li>
                <li>✓ {t("px.perk_3")}</li>
                <li>✓ {t("px.p2_t")}</li>
              </ul>
            </div>

            <div className="vd-priceblock" data-testid="enquiry-block">
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <button type="button" onClick={() => setKind("vehicle")} className="chip" data-testid="tab-enquire"
                  style={kind === "vehicle" ? { background: "var(--p)", color: "var(--p-ink)", borderColor: "var(--p)" } : {}}>
                  {t("vehicle.enquire_now")}
                </button>
                <button type="button" onClick={() => setKind("test_drive")} className="chip" data-testid="tab-testdrive"
                  style={kind === "test_drive" ? { background: "var(--p)", color: "var(--p-ink)", borderColor: "var(--p)" } : {}}>
                  {t("vehicle.book_test_drive")}
                </button>
              </div>
              <form className="enquiry-form" onSubmit={submitEnquiry} data-testid="enquiry-form">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" style={{ display: "none" }} data-testid="enq-honeypot" />
                <input placeholder={t("enquiry.your_name")} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="enq-name" />
                <input placeholder={t("enquiry.mobile")} required inputMode="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="enq-mobile" />
                <input placeholder={t("enquiry.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enq-email" />
                <textarea placeholder={t("enquiry.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="enq-message" />
                <button type="submit" disabled={sending} data-testid="enq-submit">
                  {sending ? t("enquiry.sending") : t("enquiry.submit")}
                </button>
              </form>
              <button type="button" onClick={share} className="btn-ghost" data-testid="detail-share" style={{ marginTop: 12, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Share2 size={14} /> {t("vehicle.share")}
              </button>
            </div>
            <ReserveOnline stock={v.stock_number} />
          </aside>
        </div>

        {similar.length > 0 ? (
          <section className="section" style={{ borderBottom: 0, paddingBottom: 0 }}>
            <div className="kicker" style={{ color: "var(--grey-600)" }}>{t("px.also_like")}</div>
            <h2 style={{ fontSize: 32, fontWeight: 300, letterSpacing: "-0.02em", margin: "10px 0 24px" }}>{t("vehicle.similar")}</h2>
            <div className="vgrid">
              {similar.map((sv) => <VehicleCard key={sv.stock_number} v={sv} settings={settings} />)}
            </div>
          </section>
        ) : null}
      </div>

      {/* Mobile sticky bar */}
      <div className="mobile-actions" data-testid="mobile-actions">
        <div className="mb-price">{price}</div>
        {wa ? <a href={waLink} target="_blank" rel="noopener noreferrer" className="mb-wa" data-testid="mob-wa">WhatsApp</a> : null}
        <a href="#enquiry" className="mb-enq" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="enquiry-form"]')?.scrollIntoView({ behavior: "smooth" }); }} data-testid="mob-enq">{t("px.test_drive")}</a>
      </div>
    </div>
  );
}
